// Author: Ahsan Saleem
/**
 * BloomOS primitives — the owned component vocabulary. Every operational screen
 * is composed from these, so the system reads as one environment. State is never
 * carried by colour alone: each tone pairs with an icon and a label.
 */
import type { ReactNode } from 'react';
import {
  AlertTriangle, CircleAlert, Info, CircleCheck, CircleDot, WifiOff,
  FlaskConical, Hand, Radio, Import, Sigma, Cpu, ArrowUp, ArrowDown, Minus,
} from 'lucide-react';

export type Tone = 'neutral' | 'info' | 'success' | 'caution' | 'critical' | 'sim' | 'offline';

const TONE_ICON: Record<Tone, typeof Info> = {
  neutral: CircleDot, info: Info, success: CircleCheck,
  caution: AlertTriangle, critical: CircleAlert, sim: FlaskConical, offline: WifiOff,
};

/* --------------------------------- Panel -------------------------------- */
export function Panel({ title, hint, actions, children, className = '', pad = true }: {
  title?: ReactNode; hint?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string; pad?: boolean;
}) {
  return (
    <section className={`panel ${className}`}>
      {(title || actions) && (
        <header className="panel-head">
          <div className="panel-title">{title}{hint && <span className="panel-hint">{hint}</span>}</div>
          {actions && <div className="panel-actions">{actions}</div>}
        </header>
      )}
      <div className={pad ? 'panel-body' : 'panel-body flush'}>{children}</div>
    </section>
  );
}

export function SectionLabel({ children }: { children: ReactNode }) {
  return <div className="label">{children}</div>;
}

/* --------------------------------- Badge -------------------------------- */
export function Badge({ tone = 'neutral', children, icon = true, subtle = false }: {
  tone?: Tone; children: ReactNode; icon?: boolean; subtle?: boolean;
}) {
  const Icon = TONE_ICON[tone];
  return (
    <span className={`badge tone-${tone}${subtle ? ' subtle' : ''}`}>
      {icon && <Icon size={13} strokeWidth={2.25} aria-hidden />}
      <span>{children}</span>
    </span>
  );
}

/** A bare status dot + label used inline where a full badge is too heavy. */
export function StatusDot({ tone, label }: { tone: Tone; label: ReactNode }) {
  return <span className={`statusdot tone-${tone}`}><i aria-hidden /> {label}</span>;
}

/* ------------------------------ Provenance ------------------------------ */
const PROV: Record<string, { label: string; icon: typeof Info; sim?: boolean }> = {
  MANUAL: { label: 'Manual', icon: Hand },
  SIMULATED: { label: 'Simulated', icon: FlaskConical, sim: true },
  SENSOR: { label: 'Sensor', icon: Radio },
  IMPORTED: { label: 'Imported', icon: Import },
  CALCULATED: { label: 'Calculated', icon: Sigma },
  SYSTEM: { label: 'System', icon: Cpu },
  UNKNOWN: { label: 'No data', icon: Info },
};
export function Provenance({ source }: { source?: string | null }) {
  const p = PROV[source ?? 'UNKNOWN'] ?? PROV.SYSTEM;
  const Icon = p.icon;
  return (
    <span className={`prov${p.sim ? ' is-sim' : ''}`} title={`Source: ${p.label}`}>
      <Icon size={12} strokeWidth={2.25} aria-hidden /> {p.label}
    </span>
  );
}

/* ----------------------------- Data quality ----------------------------- */
export function DataQuality({ quality, stale }: { quality: string | null; stale?: boolean }) {
  const q = stale ? 'STALE' : (quality ?? 'STALE');
  const tone: Tone = q === 'GOOD' ? 'success' : q === 'UNCERTAIN' ? 'caution' : q === 'BAD' ? 'critical' : 'offline';
  const label = q === 'STALE' ? 'Stale' : q.charAt(0) + q.slice(1).toLowerCase();
  return <StatusDot tone={tone} label={label} />;
}

/* --------------------------------- Metric ------------------------------- */
export function Metric({ label, value, unit, tone, trend, sub }: {
  label: ReactNode; value: ReactNode; unit?: ReactNode; tone?: Tone; trend?: ReactNode; sub?: ReactNode;
}) {
  return (
    <div className="metric">
      <div className="label">{label}</div>
      <div className={`metric-value tnum${tone ? ` t-${tone}` : ''}`}>
        {value}{unit && <span className="metric-unit">{unit}</span>}{trend}
      </div>
      {sub && <div className="metric-sub">{sub}</div>}
    </div>
  );
}

export function Trend({ dir, children }: { dir: 'up' | 'down' | 'flat'; children?: ReactNode }) {
  const Icon = dir === 'up' ? ArrowUp : dir === 'down' ? ArrowDown : Minus;
  return <span className={`trend trend-${dir}`}><Icon size={13} strokeWidth={2.5} aria-hidden />{children}</span>;
}

/* ------------------------------ Empty / load ---------------------------- */
export function EmptyState({ icon, title, children }: { icon?: ReactNode; title: ReactNode; children?: ReactNode }) {
  return (
    <div className="empty">
      {icon && <div className="empty-icon" aria-hidden>{icon}</div>}
      <div className="empty-title">{title}</div>
      {children && <div className="empty-body">{children}</div>}
    </div>
  );
}

export function Skeleton({ w = '100%', h = 16, r = 8 }: { w?: string | number; h?: number; r?: number }) {
  return <span className="skel" style={{ width: w, height: h, borderRadius: r }} aria-hidden />;
}
